import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Tooltip,
  IconButton,
  Grid,
  TextField,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import styles from './styles.module.css';
import DataTable from '../../utils/DataTable/';
import ListDialog from '../../popups/ListDialog';
import Info from '../../utils/Alert/Info';
import Error from '../../utils/Alert/Error';
import { SUBSCRIPTION_TYPE } from '../../../const';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import DateTimeHelpers from '../../../helpers/DateTimeHelpers';
import { ShopContext } from '../../../Context/ShopContext';
import AlertHelper from '../../../helpers/AlertHelper';
import RestrictionInfo from '../../utils/Alert/RestrictionInfo';
import StockReportService from '../../../services/StockReportService';
import withConsoleBase from '../../utils/ConsoleBase/withConsoleBase';
import CsvHelper from '../../../helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import { saveAs } from 'file-saver';
import SyncIcon from '@mui/icons-material/Sync';
import SearchIcon from '@mui/icons-material/Search';
import Grayout from '../../utils/GrayOut/Grayout';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DateTimeRange from '../../utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from '../../../services/DynamicDateRangeService';
import { roundOff } from 'helpers/NumberFormatHelper';
import { useTranslation } from 'react-i18next';

function ManualStockAdjReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [stocks, setStocks] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [viewData, setViewData] = useState('');
  const [errorMsg, setErrorMsg] = useState();
  const [isEnableSearchRefreshButton, setIsEnableSearchRefreshButton] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [prevFilterData, setPrevFilterData] = useState({
    fromDate,
    toDate,
    filterText,
  });
  const [transactions, setTransactions] = useState(null);

  const subscriptionType = shop?.subscription?.type;

  const CSV_COLUMNS = {
    plu: tCommon('PLU'),
    name: tCommon('Item Name'),
    inbound: tCommon('Inbound'),
    outbound: tCommon('Outbound'),
    stock_sales_value: {
      title: tCommon('Stock Value'),
      getValue: (item) => item?.item?.price * item?.item?.stock,
    },
  };

  const ONE_PRODUCT_CSV_COLUMNS = {
    type: tCommon('Type'),
    date: {
      title: tCommon('Date'),
      getValue: (item) => {
        return DateTimeHelpers.getDate(item.created_at) || '';
      },
    },
    time: {
      title: tCommon('Time'),
      getValue: (item) => {
        return DateTimeHelpers.getTime(item.created_at) || '';
      },
    },
    description: tCommon('Details'),
    inbound: tCommon('Inbound'),
    outbound: tCommon('Outbound'),
  };

  const loadStockReports = async ({ _fromDate = fromDate, _toDate = toDate } = {}) => {
    setLoadingIndicator(true);
    try {
      const data = {
        filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(_fromDate),
        filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(_toDate),
      };
      const resp = await StockReportService.getManualStockAdjustmentReport(data, {
        search: filterText,
      });
      setStocks(resp.data);
      setIsEnableSearchRefreshButton(false);
    } catch (err) {
      AlertHelper.setShortMessage(setErrorMsg, err.message);
    }
    setLoadingIndicator(false);
  };

  const generateStockReport = () => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('MANUAL_STAOCK_ADJ_REPORT');

    setFromDate(dynamicStartDate);
    setToDate(dynamicEndDate);

    loadStockReports({
      _fromDate: dynamicStartDate,
      _toDate: dynamicEndDate,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      fromDate: dynamicStartDate,
      toDate: dynamicEndDate,
    });
  };

  const isAnyChangeOnStockReportFilters = useCallback(() => {
    return (
      !DateTimeHelpers.isSameDate(fromDate, prevFilterData.fromDate) ||
      !DateTimeHelpers.isSameDate(toDate, prevFilterData.toDate)
    );
  }, [fromDate, toDate, prevFilterData.fromDate, prevFilterData.toDate]);

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(stocks, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `MANUAL-ADJUSTMENT-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const viewDetailsHandler = async (data) => {
    setLoadingIndicator(true);
    setViewData(data);
    try {
      const resp = await StockReportService.getItemStockTransactions(data.item_id, {
        start_date: DateTimeHelpers.getTimezoneOffsetDate(fromDate),
        end_date: DateTimeHelpers.getTimezoneOffsetDate(toDate),
        log_types: '2,3,6',
      });
      setTransactions(resp);
    } catch (err) {
      AlertHelper.setShortMessage(setErrorMsg, err.message);
    }
    setLoadingIndicator(false);
  };

  const handleCloseDetailsDialog = () => {
    setViewData(null);
    setTransactions(null);
  };

  const headerData = [
    {
      label: tCommon('PLU'),
      id: 'plu',
      type: 'text',
    },
    {
      label: tCommon('Item Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Quantity'),
      id: 'quantity',
      type: 'floatAmount',
    },
    {
      label: tCommon('Actions'),
      id: 'btnViewDetail',
      type: 'callback',
      viewRender: (item) => (
        <Tooltip title="View Details">
          <IconButton onClick={() => viewDetailsHandler(item)}>
            <RemoveRedEyeIcon className={styles.iconButton} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];
  const transactionHeader = [
    {
      label: tCommon('Type'),
      id: 'type',
      type: 'text',
    },
    {
      label: tCommon('Date'),
      id: 'Date',
      type: 'callback',
      viewRender: (item) => {
        return DateTimeHelpers.getDate(item.created_at) || '';
      },
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => {
        return DateTimeHelpers.getTime(item.created_at) || '';
      },
    },
    {
      label: tCommon('Details'),
      id: 'description',
      type: 'text',
    },
    {
      label: tCommon('Inbound'),
      id: 'inbound',
      type: 'callback',
      viewRender: (item) => (item.quantity && item.quantity > 0 ? roundOff(item.quantity) : '-'),
    },
    {
      label: tCommon('Outbound'),
      id: 'outbound',
      type: 'callback',
      viewRender: (item) => (item.quantity && item.quantity < 0 ? -1 * roundOff(item.quantity) : '-'),
    },
  ];

  useEffect(() => {
    generateStockReport();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnStockReportFilters());
  }, [isAnyChangeOnStockReportFilters, isShowGenerateButton]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Stock')}
          <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>

        {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
          <div style={{ justifyContent: 'flex-end' }}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Export As')}</label>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#00a65a' }}
              onClick={toCsvBtnPressed}
            >
              <ImportExportIcon className={styles.actionBtnIcon} />
              csv
            </Button>
          </div>
        )}
      </div>
      {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.BASIC && (
        <RestrictionInfo
          title={tCommon('Feature not available')}
          content={tCommon('To get stock report options upgrade subscription to premium')}
        />
      )}
      {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
        <>
          <div className={styles.changeable}>
            <Grid container spacing={1} alignItems="flex-end">
              <Grid item xs={12}>
                <Typography variant="h6">
                  {tReport('Manual stock adjustment report from {{fromDate}} to {{toDate}}', {
                    fromDate: DateTimeHelpers.convertDateToDMY(fromDate),
                    toDate: DateTimeHelpers.convertDateToDMY(toDate),
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <DateTimeRange
                  setFilterFromDate={setFromDate}
                  setFilterToDate={setToDate}
                  storageKey="MANUAL_STAOCK_ADJ_REPORT"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={tCommon('Search items')}
                  value={filterText}
                  onChange={(e) => {
                    setFilterText(e.target.value);
                    setIsEnableSearchRefreshButton(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      loadStockReports();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={() => loadStockReports} disabled={!isEnableSearchRefreshButton}>
                        <SearchIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="body2" style={{ marginBottom: 4, fontWeight: 500 }}>
                    {tCommon('Generate Report')}
                  </Typography>

                  <Button
                    variant="contained"
                    style={{
                      backgroundColor: '#00a65a',
                      color: '#fff',
                      height: 40,
                      minWidth: 160,
                    }}
                    onClick={generateStockReport}
                    disabled={isShowGenerateButton === false || loadingIndicator}
                    startIcon={loadingIndicator ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                  >
                    {tCommon('Generate')}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </div>
          {errorMsg && (
            <div className={styles.marginTop}>
              <Error title={errorMsg} />
            </div>
          )}
          {isShowGenerateButton === null ? (
            <Info
              title={tReport('Set filters and click generate button')}
              content={tReport(
                'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
              )}
            />
          ) : stocks && stocks.length ? (
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
              <div className={styles.tableContainer}>
                <Grayout open={isShowGenerateButton} />
                <DataTable columns={headerData} rows={stocks} rowKey="item.id" />
              </div>
            </Paper>
          ) : (
            !loadingIndicator && (
              <Info
                title={tReport('Oh no, there are data matching to your filter')}
                content={tReport(
                  'There are no data matching to your filter. Please verify your inputs to generate report'
                )}
              />
            )
          )}
          {transactions && (
            <ListDialog
              onClose={handleCloseDetailsDialog}
              toggleItemsTab={viewDetailsHandler}
              tableHeader={transactionHeader}
              rowData={transactions}
              title={
                viewData
                  ? tReport('Manual stock adjustment details of {{name}}', { name: viewData.name })
                  : tReport('stock details')
              }
              emptyDataContent={tReport('No transactions found')}
              csvColumns={ONE_PRODUCT_CSV_COLUMNS}
            />
          )}
        </>
      )}
    </div>
  );
}

export default withConsoleBase(ManualStockAdjReport);
